import React, { useContext } from "react";
import "./Calculator.css";

import Grid from "@material-ui/core/Grid";
import Button from "@material-ui/core/Button";
import ToggleButtonGroup from "@material-ui/lab/ToggleButtonGroup";
import ToggleButton from "@material-ui/lab/ToggleButton";
import Typography from "@material-ui/core/Typography";
import FormHelperText from "@material-ui/core/FormHelperText";
import TextField from "@material-ui/core/TextField";
import { useForm, Controller } from "react-hook-form";
import { withRouter } from "react-router-dom";
import CrclContext from "../contexts/CrclContext";
import { DataUsageSharp } from "@material-ui/icons";

const Calculator = (props) => {
  const crclcontext = useContext(CrclContext);

  function calculateIdealWeight(height, sex) {
    //calculate ideal weight = (height-152)*0.9+50
    return Number(((height - 152) * 0.9 + (sex === "male" ? 50 : 45.5)).toFixed(3));
  }

  function calculateCrCl(age, weight, creatinine, sex){
    let crcl = ((140 - age) * weight) / (0.814 * creatinine);
    return sex === "male" ? crcl : crcl * .85;
  }

  function getCrClSub(crcl){
    if (crcl > 50) {
      return "";
    } else if (crcl < 50 && crcl >= 30) {
      return "Moderate renal impairment";
    } else if (crcl < 30 && crcl >= 0.1) {
      return "Severe renal impairment";
    }
  }

  function getCrClSubClass(crcl){
    if (crcl > 50) {
      return "green";
    } else if (crcl < 50 && crcl >= 30) {
      return "yellow";
    } else if (crcl < 30 && crcl >= 0.1) {
      return "red";
    }
  }

  const calculate = (e, data) => {
    //Validation text
    let validationText = {};
    let alertDialog = false;
    let alertDialogText = [];
    let alertTitle = "";

    //Start of calculations
    let idealWeight;
    let crcl;
    let crclIdeal;
    let crclSub;
    let crclClass;
    let crclSubActual;
    let crclSubIdeal;
    let anticoagulationText = [];
    let prophylaxisText = [];
    let prophylaxisDose;

    idealWeight = calculateIdealWeight(data.height, data.sex);

    //Calculate CrCl
    crcl = calculateCrCl(data.age, data.weight, data.creatinine, data.sex);
    crclIdeal = calculateCrCl(data.age, idealWeight, data.creatinine, data.sex);

    // DG TESTING
    console.log('weight', data.weight);
    console.log('idealWeight', idealWeight);
    console.log('crcl', crcl);
    console.log('crclIdeal', crclIdeal);

    //Define CrCl warning text
    const lowerCrCl = Math.min(crcl, crclIdeal);
    crclSub = getCrClSub(lowerCrCl);
    crclClass = getCrClSubClass(lowerCrCl);
    crclSubIdeal = getCrClSub(crclIdeal);
    crclSubActual = getCrClSub(crcl);

    // New structure
    // Full Anti-Coagulation
    const weightOutsideRange = (data.weight < 50 || data.weight > 105);
    const severeRenalText = <Typography variant="body2"><p>as patient has severe renal impairment.</p></Typography>;
    const severeRenalWeightText = <Typography variant="body2"><p>as patient has severe renal impairment and is underweight, overweight or obese.  CrCl estimates may also be inaccurate and evidence for use of enoxaparin is limited at extremes of body weight.</p></Typography>;
    const severeRenalHeparinText = [<Typography variant="h6" color="primary">
                                      Use IV Heparin
                                    </Typography>,
                                    weightOutsideRange ? severeRenalWeightText : severeRenalText,
                                    <Typography variant="body1" style={{ marginTop: 20 }}>
                                      <strong>IV HEPARIN</strong>
                                    </Typography>,
                                    <Typography variant="body2">
                                      <p>
                                        IV Heparin treatment dose and monitoring is to occur in accordance
                                        with
                                      </p>
                                      <p>
                                        <strong>
                                          <a target="_blank" href="https://qheps.health.qld.gov.au/__data/assets/pdf_file/0040/1797961/sw014-heparin-form.pdf">Adult Heparin intravenous infusion order and administration form</a>
                                        </strong>
                                      </p>
                                    </Typography>,

                                    <Typography variant="body1" style={{ marginTop: 20 }}>
                                      <strong>ENOXAPARIN</strong>
                                    </Typography>,
                                    <Typography variant="body2">
                                      <p>Enoxaparin treatment dose based upon consultant advice.</p>
                                      <p>
                                        If enoxaparin is selected, discuss with consultant prior to
                                        prescribing.
                                      </p>
                                      <p>
                                        <strong>
                                          Documented consultant approval is required for therapeutic
                                          enoxaparin use in patients if CrCl less than 30mL/min.
                                        </strong>
                                      </p>
                                    </Typography>,
                                    <Typography variant="body1" style={{ marginTop: 20 }}>
                                      <strong>ENOXAPARIN MONITORING</strong>
                                    </Typography>,
                                    <Typography variant="body2">
                                      <p>
                                        If enoxaparin is used, and treatment is to be continued beyond 48
                                        hours:
                                      </p>
                                    </Typography>,
                                    <Typography variant="h6" color="primary">
                                      Measure anti-factor Xa level (4hours post dose) after 3rd or 4th
                                      dose (to coincide with laboratory hours) and then measure every
                                      second day.
                                    </Typography>];

    const dosageText = [<Typography variant="h6" color="primary">
                          Use Enoxaparin or IV Heparin
                        </Typography>,
                        <Typography gutterBottom variant="body1" style={{ marginTop: 20 }}>
                          <strong>
                            ENOXAPARIN <br /> TREATMENT DOSE IS
                          </strong>
                        </Typography>,
                        <Typography variant="h6" color="primary">
                          {Math.round((data.weight - 1) / 10) * 10} mg TWICE DAILY
                        </Typography>,
                        <Typography variant="body2">
                          <p>Round weight to nearest 10kg when dosing and round down if interval of 5kg</p>
                        </Typography>];

    const moderateRenalMonitoringText = [<Typography variant="body1" style={{ marginTop: 20 }}>
                                          <strong>ENOXAPARIN MONITORING</strong>
                                        </Typography>,
                                        <Typography variant="body2">
                                          <p>If treatment is to be continued beyond 48 hours:</p>
                                        </Typography>,
                                        <Typography variant="h6" color="primary">
                                          Measure anti-factor Xa level (4hours post dose) after 3rd or 4th
                                          dose (to coincide with laboratory hours) and then measure every
                                          second day.
                                        </Typography>,
                                        <Typography variant="body2">
                                          <p>
                                            Strongly consider IV heparin in extremes of body size, severe
                                            liver disease, diseases of skeletal muscle and elderly patients
                                            with low body weight (as CrCl may be inaccurate).
                                          </p>
                                          <p>
                                            Use IV heparin if anti-factor Xa measurements are unavailable or
                                            renal function is unstable (e.g. acute kidney failure).
                                          </p>
                                        </Typography>,
                                        <Typography variant="body1" style={{ marginTop: 20 }}>
                                          <strong>IV HEPARIN</strong>
                                        </Typography>,
                                        <Typography variant="body2">
                                          <p>
                                            IV Heparin treatment dose and monitoring is to occur in accordance
                                            with
                                          </p>
                                          <p>
                                            <strong>
                                              <a target="_blank" href="https://qheps.health.qld.gov.au/__data/assets/pdf_file/0040/1797961/sw014-heparin-form.pdf">Adult Heparin intravenous infusion order and administration form</a>
                                            </strong>
                                          </p>
                                        </Typography>];

    const minorRenalMonitoringText = [<Typography variant="body1" style={{ marginTop: 20 }}>
                                        <strong>ENOXAPARIN MONITORING</strong>
                                      </Typography>,
                                      <Typography variant="body2">
                                        <p>If treatment is to be continued beyond 5 days: </p>
                                      </Typography>,
                                      <Typography variant="h6" color="primary">
                                        Measure anti-factor Xa level (4hours post dose) on day 5
                                        (to coincide with laboratory hours) and then measure every
                                        second day.
                                      </Typography>,

                                      <Typography variant="body2">
                                        <p>
                                          Strongly consider IV heparin in extremes of body size, severe
                                          liver disease, diseases of skeletal muscle and elderly patients
                                          with low body weight (as CrCl may be inaccurate).
                                        </p>
                                        <p>
                                          Use IV heparin if anti-factor Xa measurements are unavailable or
                                          renal function is unstable (e.g. acute kidney failure).
                                        </p>
                                      </Typography>,
                                      <Typography variant="body1" style={{ marginTop: 20 }}>
                                        <strong>IV HEPARIN</strong>
                                      </Typography>,
                                      <Typography variant="body2">
                                        <p>
                                          IV Heparin treatment dose and monitoring is to occur in accordance
                                          with
                                        </p>
                                        <p>
                                          <strong>
                                            <a target="_blank" href="https://qheps.health.qld.gov.au/__data/assets/pdf_file/0040/1797961/sw014-heparin-form.pdf">Adult Heparin intravenous infusion order and administration form</a>
                                          </strong>
                                        </p>
                                      </Typography>];

    const weightOutsideRangeText = [<Typography variant="h6" color="primary">
                                      IV Heparin is preferred agent
                                    </Typography>,
                                    <Typography variant="body2">
                                      <p>
                                        because patient is underweight, overweight or obese.
                                        CrCl estimates may also be inaccurate and evidence 
                                        for use of enoxaparin is limited at extremes of body weight.
                                      </p>
                                    </Typography>,
                                    <Typography variant="body1" style={{ marginTop: 20 }}>
                                      <strong>IV HEPARIN</strong>
                                    </Typography>,
                                    <Typography variant="body2">
                                      <p>
                                        IV Heparin treatment dose and monitoring is to occur in accordance
                                        with
                                      </p>
                                      <p>
                                        <strong>
                                          <a target="_blank" href="https://qheps.health.qld.gov.au/__data/assets/pdf_file/0040/1797961/sw014-heparin-form.pdf">Adult Heparin intravenous infusion order and administration form</a>
                                        </strong>
                                      </p>
                                      <p>
                                        If IV access is not possible, discuss use of enoxaparin with
                                        consultant prior to prescribing.
                                      </p>
                                    </Typography>,
                                    <Typography variant="body1" style={{ marginTop: 20 }}>
                                      <strong>ENOXAPARIN</strong>
                                    </Typography>,
                                    <Typography variant="body2">
                                      <p>Enoxaparin treatment dose based upon consultant advice.</p>
                                      <p>
                                        If enoxaparin is selected, discuss with consultant prior to
                                        prescribing.
                                      </p>
                                      <p>
                                        Note: Enoxaparin dose-capping may limit supratherapeutic levels but
                                        may result in under-dosing. Weight based enoxaparin dosing using
                                        actual body weight may result in over-dose and bleeding. Ensure
                                        monitoring if enoxaparin is used.
                                      </p>
                                    </Typography>,
                                    <Typography variant="body1" style={{ marginTop: 20 }}>
                                      <strong>ENOXAPARIN MONITORING</strong>
                                    </Typography>,
                                    <Typography variant="body2">
                                      <p>If treatment is to be continued beyond 48 hours:</p>
                                    </Typography>,
                                    <Typography variant="h6" color="primary">
                                      Measure anti-factor Xa level (4hours post dose) after 3rd or 4th dose
                                      (to coincide with laboratory hours) and then measure every second day.
                                    </Typography>];                                

    if (lowerCrCl < 30) {
      // anticoagulationText.push(weightOutsideRange ? severeRenalWeightText : severeRenalText);
      anticoagulationText.push(severeRenalHeparinText);
    } else if (lowerCrCl >= 30 && lowerCrCl <= 50) {
      weightOutsideRange 
      ? anticoagulationText.push(weightOutsideRangeText) 
      : anticoagulationText.push(dosageText, moderateRenalMonitoringText);
    } else if (lowerCrCl > 50) {
      weightOutsideRange 
      ? anticoagulationText.push(weightOutsideRangeText) 
      : anticoagulationText.push(dosageText, minorRenalMonitoringText);
    }

    //Prophylaxis

    if (crcl > 49 || lowerCrCl > 15) {
      prophylaxisText.push(
        <Typography variant="body1">
          <strong>
            ENOXAPARIN <br />
            PROPHYLATIC DOSE{" "}
          </strong>
        </Typography>
      );
    }

    if (lowerCrCl <= 30) {
      prophylaxisDose = "20mg";
    } else if (lowerCrCl > 30) {
      prophylaxisDose = +data.weight > 49 ? "40mg" : "20mg";
    } else {
      prophylaxisDose = "20mg";
    }

    // DG Removed condition as seems irrelevant with new spec.
    // Always "ONCE DAILY"
    // if (crcl > 49 || lowerCrCl > 15) {
      prophylaxisText.push(
        <Typography variant="h5" color="primary">
          {prophylaxisDose} ONCE DAILY
        </Typography>
      );
    // }

    prophylaxisText.push(
      <Typography variant="body1" style={{ marginTop: 20 }}>
        <p>Use chemoprophylaxis according to VTE risk.</p>
      </Typography>,
      <Typography variant="body1" style={{ marginTop: 20 }}>
        <p><a target="_blank" href="https://qheps.health.qld.gov.au/__data/assets/pdf_file/0035/2397554/002897.pdf">Refer to VTE guidelines.</a></p>
      </Typography>,
      <Typography variant="body1" style={{ marginTop: 20 }}>
        <strong>ENOXAPARIN MONITORING</strong>
      </Typography>,
      <Typography variant="body2">
        <p>
          Routine monitoring of anti-factor Xa is not recommended for patients
          on enoxaparin prophylaxis.{" "}
        </p>
        <p>
          If any significant bleeding occurs, enoxaparin should be stopped and
          urgent consultant review organised. Anti-factor Xa measurement may
          assist with management.
        </p>
      </Typography>
    );

    //Validation warning that don't block the calculation
    if (
      data.weight > 200 ||
      data.height < 152 ||
      data.height > 200 ||
      data.age > 100 ||
      lowerCrCl < 60
    ) {
      alertDialog = true;

      alertTitle = "Warning";
      alertDialogText.push(
        <Typography variant="body1" paragraph id="alert-dialog-warning">
          <strong>
            Prior to prescribing or administering an anticoagulant, please
            ensure duplication of therapeutic anticoagulation does not occur.

            If the patient is already on an anticoagulant, 
            check for appropriate changeover and administration times.
          </strong>
        </Typography>,
      );
    }

    validationText = {
      warningText: alertDialogText,
    };

    crclcontext.update({
      validationText: validationText,
      alertDialog: alertDialog,
      alertTitle: alertTitle,
      crcl: crcl.toFixed(1),
      crclIdeal: crclIdeal.toFixed(1),
      crclSub: crclSub,
      crclClass: crclClass,
      crclSubActual: crclSubActual,
      crclSubIdeal: crclSubIdeal,
      idealWeight: idealWeight,
      anticoagulationText: anticoagulationText,
      prophylaxisText: prophylaxisText,
    });

    e.preventDefault();
    props.history.push("/results");
  };

  const defaultValues = {
    age: '',
    weight: '',
    height: '',
    creatinine: '',
  };

  const { handleSubmit, register, errors, setValue, reset, control } = useForm({
    mode: "onChange",
    defaultValues: defaultValues,
  });

  const onSubmit = (data, e) => {
    if (!data.sex || data.sex === null) {
      let validationText = { sexHelperText: "Select a sex" };
      crclcontext.update({ validationText: validationText, ...data });
      return;
    }
    crclcontext.update({ ...data });
    calculate(e, data);
  };

  const [sex, setSex] = React.useState(() => crclcontext.sex);
  const handleSex = (e, newSex) => {
    setSex(newSex);
    setValue("sex", newSex);
  };

  React.useEffect(() => {
    register("sex");
  }, [register]);

  React.useEffect(() => {
    const formData = { ...crclcontext };
    setValue("sex", formData.sex);
    setValue("age", formData.age);
    setValue("weight", formData.weight);
    setValue("height", formData.height);
    setValue("creatinine", formData.creatinine);
  }, [setValue, crclcontext]);

  const resetHandler = () => {
    handleSex({}, null);
    reset();
  };

  return (
    <div className="calculator">
      <Typography variant="body1" gutterBottom>
        All fields are required.
      </Typography>
      <Typography fontSize="14" variant="body2" gutterBottom>
        Sex *
      </Typography>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <ToggleButtonGroup
              name="sex"
              value={sex}
              size="large"
              exclusive
              required
              className="toggle"
              onChange={handleSex}
              aria-label="select sex"
            >
              <ToggleButton value="male" aria-label="Male" className="toggle">
                Male
              </ToggleButton>
              <ToggleButton
                value="female"
                aria-label="Female"
                className="toggle"
              >
                Female
              </ToggleButton>
            </ToggleButtonGroup>
            <FormHelperText error id="my-helper-text">
              {crclcontext.validationText.sexHelperText}
            </FormHelperText>
          </Grid>
          <Grid item xs={12}>
            <Controller
              as={<TextField aria-label="age" />}
              control={control}
              name="age"
              label="Age" 
              type="number" 
              variant="filled"
              fullWidth
              required
              rules={{
                min: { value: 18, message: "Enter an age above 18" },
                max: { value: 100, message: "Enter an age below 100" },
              }}
              error={errors.age ? true : false}
              helperText={errors.age?.message}
            />
          </Grid>
          <Grid item xs={6}>
            <Controller
              as={
                <TextField aria-label="weight" />
              }
              control={control}
              name="weight"
              label="Weight (kg)" 
              type="number"
              variant="filled"
              fullWidth
              required
              rules={{
                min: { value: 20, message: "Weight is too low" },
                max: { value: 500, message: "Weight is too high" },
              }}
              error={errors.weight ? true : false}
              helperText={errors.weight?.message}
            />
          </Grid>
          <Grid item xs={6}>
            <Controller
              as={
                <TextField aria-label="height" />
              }
              control={control}
              name="height"
              label="Height (cm)" 
              type="number"
              variant="filled"
              fullWidth
              required
              rules={{
                min: { value: 152, message: "Height is too low" },
                max: { value: 300, message: "Height is too high" },
              }}
              error={errors.height ? true : false}
              helperText={errors.height?.message}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              as={
                <TextField aria-label="creatinine" />
              }
              control={control}
              name="creatinine"
              label="Serum creatinine (umol/L)" 
              type="number"
              variant="filled"
              fullWidth
              required
              rules={{}}
              error={errors.creatinine ? true : false} 
              helperText={errors.creatinine?.message} 
            />
          </Grid>
          <Grid container justify="space-between" className="buttonClass">
            <Button
              variant="outlined"
              color="primary"
              type="reset"
              onClick={resetHandler}
            >
              Reset
            </Button>
            <Button variant="contained" color="primary" type="submit">
              Calculate CrCl
            </Button>
          </Grid>
        </Grid>
      </form>
    </div>
  );
};

export default withRouter(Calculator);
